import { useMutation, useQuery } from '@apollo/client';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import Loader from 'Components/Forms/Loader';
import useMessages from 'hooks/useMessages';
import Container from 'FeatureModules/Setting/Components/Container';
import WarningMessage from 'Components/WarningMessage';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import { UPDATE_COMPANY } from 'graphql/secure/mutations/company.mutations';
import { GET_COMPANY_DETAILS } from 'graphql/secure/queries';
import React from 'react';
import { ECustomerPlan, ICompany } from 'FeatureModules/Auth/types/company.types';
import ErrorState from 'Components/ErrorState';
import CompanyEditForm from '../Components/Forms/CompanyEditForm';
import SettingsHeader from '../Components/SettingsHeader';
import AddressForm from '../Components/Forms/AddressForm';

const AlertContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiAlert-root': {
    boxShadow: 'none',
    borderColor: theme.palette.warning.main,
  },

  '& .MuiAlert-icon ': {
    color: theme.palette.warning.main,
  },
}));

const CompanyDetails = () => {
  const { plan } = useCompanyPlanContext();
  const { data: { company } = {}, error, loading } = useQuery(GET_COMPANY_DETAILS);
  const isPartner = plan === ECustomerPlan.FREEMIUM;

  const { setSuccessMessage, setErrorMessage } = useMessages();
  const [updateCompany] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      setSuccessMessage('Success! Company details were updated.');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your companies details.');
    },
  });

  const handleSubmit = (input: Partial<ICompany>) => {
    return updateCompany({
      variables: {
        input,
      },
    });
  };

  if (error) return <ErrorState error={error} />;
  if (!company && loading) return <Loader />;

  return (
    <>
      {!company.dataValid && !isPartner && (
        <AlertContainer>
          <WarningMessage>
            In order to publish products you need to provide your company details.
          </WarningMessage>
        </AlertContainer>
      )}
      <Container>
        <SettingsHeader title="Company details" />
        <CompanyEditForm onSubmit={handleSubmit} company={company} />
      </Container>
      <Container>
        <SettingsHeader title="Address" />
        <AddressForm onSubmit={handleSubmit} address={company.address}>
          {({ submitForm, isSubmitting }) => (
            <Grid container item justifyContent="flex-end" alignItems="center">
              <ThemeButton
                size="large"
                color="PRIMARY"
                loading={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                Save
              </ThemeButton>
            </Grid>
          )}
        </AddressForm>
      </Container>
    </>
  );
};

export default CompanyDetails;
